import React from 'react';
import classnames from 'classnames';
import { serverTimeToLocal, localTimeToServer, getDatetimeFormat as getDateTimeFormat, getSysFieldid, getWeekStartDay } from '@platform/api';
import BaseDatePicker, { RangePicker as BaseRangePicker } from './PickerInput';
import { DateFunction, DateUtil } from './util';
import { useLocale } from './hoc';
import dayjs from 'dayjs';
import AdvancedFormat from 'dayjs/plugin/updateLocale';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
dayjs.extend(AdvancedFormat);

import './style/adapter.less';

export const getFunctionValue = DateFunction.getFunctionValue;
export const getFunctionDisplay = DateFunction.getFunctionDisplay;

let defaultDateFormat = ['YYYY-MM-DD', 'YYYYMMDD', 'YYYY.MM.DD', 'YYYY/MM/DD', 'YYYY MM DD', 'YYMMDD'];
let defaultDateTimeFormat = defaultDateFormat.map(format => format + ' HH:mm:ss');

function toArray(value) {
    return Array.isArray(value) ? value : [value];
}

/**
 * ================================== hooks ==================================
 */

function useFieldId(props, range) {
    let { dropdownClassName, fieldid } = props;

    let rangeProps = {};
    if(range){
        rangeProps = {
            pickerInputProps: [{ fieldid: getSysFieldid('leftInput') }, { fieldid: getSysFieldid('rightInput') }],
            pickerPanelProps: [{ fieldid: getSysFieldid('range-left') }, { fieldid: getSysFieldid('range-right') }],
        };
    }

    return {
        ...props,
        dropdownClassName: classnames(dropdownClassName, { [`fieldid_${fieldid}_calendar`]: !!getSysFieldid(fieldid) }),
        pickerWrapperProps: { fieldid: getSysFieldid(fieldid) },
        otherPickerProps: { okBtn: { fieldid: 'ok_btn' } },
        ...rangeProps,
    };
}

function useDefaultProps(props) {
    let { 
        format, 
        showTime, 
        picker = 'date', 
        renderFooter, 
        weekStart, 
        onOpenChange, 
        showClose, 
        showClear, 
        onEnter,
        onKeyDown,
        renderInTable,
        autoOpenWhenFocus,
        className,
        showStar,
    } = props;
    if (showTime) {
        picker === 'date' && (picker = 'dateTime');
    } else if (showTime === false) {
        picker === 'dateTime' && (picker = 'date');
    }
    let defaultFormat = _getDefaultFormat({ ...props, picker }),
        customFormatEnable = true;

    if (format) { //props上配置了format，需要处理time的格式
        customFormatEnable = false;
    } else { //默认的format没有不识别的格式
        format = defaultFormat;
    }
    !Array.isArray(format) && (format = [format]);

    if (props.hasOwnProperty('weekStart')) {
        weekStart = props.weekStart;
    } else {
        // 根据语种自动从环境中获取
        weekStart = getWeekStartDay();
    }

    weekStart = Math.max(0, Math.min(weekStart, 6));

    return {
        ...props,
        format,
        picker,
        renderExtraFooter: renderFooter, 
        weekStart,
        customFormatEnable,
        onOpen: () => {
            onOpenChange?.(true);
        },
        onClose: () => {
            onOpenChange?.(false);
        },
        allowClear: showClose === undefined ? showClear === undefined ? true : showClear : showClose,
        onKeyDown: event => {
            if (event.which === 13) {
                onEnter?.(event);
            }
            onKeyDown?.(event);
        },
        autoOpenWhenFocus: props.hasOwnProperty('autoOpenWhenFocus') ? autoOpenWhenFocus : !renderInTable,
        className: classnames(className, { 'picker-required': showStar }),
        // showTimeFunction: true,
    };
}

function useCustomFormat(props) {
    let { format, value, customFormatEnable } = props;
    let nextProps = {};
    let originFormat = format[0];

    let customFormat;
    if (customFormatEnable) { //props上没有format，取全局的format
        customFormat = getDateTimeFormat(originFormat).replace('[]', ''); 
        format = [customFormat, ...format];
    } else {
        customFormat = originFormat;
    }

    if (props.hasOwnProperty('value')) {
        if (Array.isArray(value)) {
            nextProps.value = value.map(value => {
                if (value && !/(#[^#]*#)/.test(value)) {
                    if(DateUtil.isUnSupportFormat(originFormat)){ // 在props上配置了MMM的尝试识别
                        let [unSupportFormat, ...other] = format;
                        let day = DateUtil.getUnSupportFormatDate(value, unSupportFormat);
                        if(day?.isValid()) return day.format(customFormat); //没有识别成功的，还要尝试其他的方式
                        return dayjs(value, other).format(customFormat);
                    }
                    return dayjs(value, originFormat).format(customFormat);
                }
                return value;
            });
        } else {
            if (value && !/(#[^#]*#)/.test(value)) {
                if(DateUtil.isUnSupportFormat(originFormat)){ // 在props上配置了MMM的尝试识别
                    let [unSupportFormat, ...other] = format;
                    let day = DateUtil.getUnSupportFormatDate(value, unSupportFormat);
                    nextProps.value = day?.isValid() ? day.format(customFormat) : dayjs(value, other).format(customFormat);
                } else {
                    nextProps.value = dayjs(value, originFormat).format(customFormat);
                }
            }
        }
    }

    nextProps.format = format;
    nextProps.onChange = value => {
        if (Array.isArray(value)) {
            value = value.map(value => {
                if (value && !/(#[^#]*#)/.test(value)) {
                    if(DateUtil.isUnSupportFormat(format)){
                        let [unSupportFormat, ...other] = format;
                        let day = DateUtil.getUnSupportFormatDate(value, unSupportFormat);
                        if(day?.isValid()) return day.format(originFormat);
                        return dayjs(value, other).format(originFormat);
                    }
                    return dayjs(value, format).format(originFormat);
                }
                return value;
            });
        } else {
            if (value && !/(#[^#]*#)/.test(value)) {
                if(DateUtil.isUnSupportFormat(format)){
                    let [unSupportFormat, ...other] = format;
                    let day = DateUtil.getUnSupportFormatDate(value, unSupportFormat);
                    value = day?.isValid() ? day.format(originFormat) : dayjs(value, other).format(originFormat);
                } else {
                    value = dayjs(value, format).format(originFormat);
                }
            }
        }
        props.onChange?.(value);
    };

    return {
        ...props,
        ...nextProps,
    };
}

// 获取多时区时的value和onChange
function useTimeZone(props) {
    let {
        panelValue,
        isconverttimezones,
        format,
    } = props;

    let nextProps = {
        onChange: value => {
            value = _getOutputValue(props, value);
            props.onChange?.(value);
        },
    };
    if (props.hasOwnProperty('value')) {
        nextProps.value = _getInputValue(props);
    }
    
    panelValue = serverTimeToLocal(dayjs(panelValue || new Date()), isconverttimezones).format(format[0]);

    return {
        ...props,
        ...nextProps,
        base: panelValue,
    };
}

function useTimeSuffix(props, { suffix, excludes = ['#past#', '#future#'] }) {
    let { value, onChange } = props;
    let nextProps = {};

    if (props.hasOwnProperty('value')) {
        if (Array.isArray(value)) {
            nextProps.value = value.map(value => value && value.split(' ')[0]);
        } else {
            nextProps.value = value && value.split(' ')[0];
        }
    }

    nextProps.onChange = value => {
        if (typeof suffix === 'function') {
            suffix = suffix(value);
        }
        if (Array.isArray(value)) {
            value = value.map((value, index) => (excludes.includes(value) || !value) ? value : (value + ' ' + suffix[index]));
        } else {
            value = (excludes.includes(value) || !value) ? value : (value + ' ' + suffix);
        }
        onChange?.(value);
    };

    return {
        ...props,
        ...nextProps, 
    };
}

function useFunctions(props, range, singleValue){
    if (!props.showTimeFunction || !props.picker.includes('date')) {
        return props;
    }

    const { hiddenFunList, functions, emptyFunList } = props;

    let defaultFunctions = DateFunction.getDefaultDateFunction({ ...props, range, singleValue });

    // if(!range && !singleValue){ //单点日期（非start / end）没有的函数
    //     notSingleFunction.forEach(key => delete defaultFunctions[key]);

    // } else {
    if(emptyFunList){ //有值时，true为至过去和至未来都要，false都不要，数组时按数组内容显示
        if(Array.isArray(emptyFunList)){
            let testSet = new Set(emptyFunList);
            if(!testSet.has('#past#')){
                delete defaultFunctions['#past#'];
            }
            if(!testSet.has('#future#')){
                delete defaultFunctions['#future#'];
            }
        }
    } else {
        delete defaultFunctions['#past#'];
        delete defaultFunctions['#future#'];
    }
    // }

    let newFunList = { 
        ...defaultFunctions, 
        ...functions, 
    };
    
    if (Array.isArray(hiddenFunList)) {
        hiddenFunList.forEach(element => {
            delete newFunList[element];
        });
    } else if(hiddenFunList) {
        newFunList = [];
    }

    return { 
        ...props,
        functions: newFunList, 
    };
}

function _getDefaultFormat(props) {
    if (props.showTime) {
        return defaultDateTimeFormat;
    }
    switch (props.picker) {
        case 'date':
            return defaultDateFormat;           
        case 'time':
            return ['HH:mm:ss'];           
        case 'dateTime':
            return defaultDateTimeFormat;           
        case 'month':
            return ['YYYY-MM'];           
        case 'year':
            return ['YYYY'];           
        default:
            return defaultDateFormat;
    }
}

// 处理多时区的输入
function _getInputValue(props) {
    let { isconverttimezones, value, format } = props;

    function translate(value) {
        if (/(#[^#]*#)/.test(value)) {
            return value;
        }
        // TODO:YYYY-MM-DD HH:mm:ss是否需要兼容
        return serverTimeToLocal(dayjs(value, ['YYYY-MM-DD HH:mm:ss'].concat(format)), isconverttimezones).format(format[0]);
    }

    if (Array.isArray(value)) {
        return value.map(value => value && translate(value));
    } else {
        return value && translate(value);
    }
}

// 处理多时区的输出
function _getOutputValue(props, value) {
    let { isconverttimezones, format } = props;

    function translate(value) {
        if (/(#[^#]*#)/.test(value)) {
            return value;
        }
        // TODO:YYYY-MM-DD HH:mm:ss是否需要兼容
        return localTimeToServer(dayjs(value, ['YYYY-MM-DD HH:mm:ss'].concat(format)), isconverttimezones).format('YYYY-MM-DD HH:mm:ss');
    }
    
    if (Array.isArray(value)) {
        return value.map(value => value && translate(value));
    } else {
        return value && translate(value);
    }
}

/**
 * ================================== adapterCreator ==================================
 */
// adapter的顺序很重要，前面的可以提供属性给后面的用
const adapterCreator = adapters => props => adapters.reduce((props, hoc) => {
    if (Array.isArray(hoc)) {
        let [target, ...params] = hoc;
        return target?.(props, ...params) || props;
    }
    return hoc?.(props) || props;
}, props);

/**
 * ================================== tinper-bee适配器 ==================================
 */
function BeeDatePicker(props) {
    let { renderIcon, showClose, showClear, ...others } = props;

    return <BaseDatePicker 
        {...others}
        renderInputIcon={renderIcon}
        allowClear={showClose || showClear}
    />;
}

function BeeRangePicker(props) {
    return <BaseRangePicker {...props} />;
}

function BeeTimePicker(props) {
    return <BaseDatePicker {...props} picker="time" allowClear={props.hasOwnProperty('allowEmpty') ? props.allowEmpty : true}/>;
}

function BeeMonthPicker(props) {
    return <BaseDatePicker {...props} picker="month" />;
}

function BeeYearPicker(props) {
    return <BaseDatePicker {...props} picker="year" />;
}

/**
 * ================================== 单点日期适配器 ==================================
 */

/**
 * 1、不能选时间
 * 2、支持自定义格式
 * 3、不转时区
 */
export const DatePicker = useLocale(function(props) {
    props = adapterCreator([
        useFieldId,
        useDefaultProps, 
        useFunctions,
        useCustomFormat,
    ])(props);
    return <BeeDatePicker {...props} onSelect={date => {
        props.onSelect?.(dayjs(date));
    }} />;
});

/**
 * 1、时间默认为00:00:00，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 * 
 * 输入(value)和输出(onChange)都是服务器时间
 * 显示(传给组件)是当地时间
 */
export const DatePickerTimeZoneConversion = useLocale(function(props) {
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        useFunctions, 
        useTimeZone, 
        [useTimeSuffix, { suffix: '00:00:00' }],
        useCustomFormat,
    ])(props);
    return <BeeDatePicker {...props} />;
});

/**
 * 1、时间默认为当前时间，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const DatePickerClient = useLocale(function(props) {
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        useFunctions, 
        useTimeZone, 
        [useTimeSuffix, {
            suffix: () => {
                let now = new Date(),
                    nowTime = [now.getHours(), now.getMinutes(), now.getSeconds()];
                nowTime = nowTime.map(value => value < 10 ? '0' + value : value);
                return nowTime.join(':');
            },
        }],
        useCustomFormat,
    ])(props);
    let { className } = props;
    return <BeeDatePicker
        {...props}
        className={`DatePickerClient ${className}`}
    />;
});

/**
 * 1、没有时间
 * 2、不支持自定义格式
 * 3、无时区
 */
export const DatePickerNoTimeZone = useLocale(function(props) {
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        useFunctions,
        useCustomFormat,
    ])(props);
    return <BeeDatePicker {...props} />;
});

/**
 * 1、时间默认为当前时间，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const TZDatePickClientHourTime = useLocale(function(props) {
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        useFunctions, 
        useTimeZone, 
        [useTimeSuffix, {
            suffix: () => {
                let now = new Date(),
                    nowTime = [now.getHours(), now.getMinutes(), now.getSeconds()];
                nowTime = nowTime.map(value => value < 10 ? '0' + value : value);
                return nowTime.join(':');
            },
        }],
        useCustomFormat,
    ])(props);
    return <BeeDatePicker
        {...props}
        className={`DatePickClientHourTime ${props.className}`}
    />;
});

/**
 * 1、可选时间
 * 2、支持自定义格式
 * 3、转时区
 */
export const TZDatePickClientTime = useLocale(function(props) {
    props = { ...props, picker: "dateTime" };
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        useFunctions,
        useTimeZone, 
        useCustomFormat,
    ])(props);
    let {
        className,
        ...others
    } = props;
    return <BeeDatePicker 
        {...others}
        className={`DatePickClientTime ${className}`}
    />;
});

/**
 * 1、时间默认为23:59:59，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const TZDatePickerEnd = useLocale(function(props) {
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        [useFunctions, false, 'end'], 
        useTimeZone, 
        [useTimeSuffix, { suffix: '23:59:59' }],
        useCustomFormat,
    ])(props);
    return <BeeDatePicker 
        {...props}
        className={`TZDatePickerEnd ${props.className}`}
    />;
});

/**
 * 1、时间默认为00:00:00，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const TZDatePickerStart = useLocale(function(props) {
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        [useFunctions, false, 'start'], 
        useTimeZone, 
        [useTimeSuffix, { suffix: '00:00:00' }],
        useCustomFormat,
    ])(props);
    return <BeeDatePicker 
        {...props}
        className={`TZDatePickerStart ${props.className}`}
    />;
});

/**
 * ================================== 日期区间适配器 ==================================
 */

/**
 * 1、没有时间
 * 2、支持自定义格式
 * 3、不转时区
 */
export const RangePicker = useLocale(function(props) {
    props = adapterCreator([
        [useFieldId, { range: true }],
        useDefaultProps,  
        [useFunctions, true], 
        useCustomFormat,
    ])(props);
    return <BeeRangePicker picker="date" {...props} />;
});

/**
 * 1、时间默认为00:00:00-23:59:59，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const RangePickerClient = useLocale(function(props) {
    props = adapterCreator([
        [useFieldId, { range: true }],
        useDefaultProps,  
        [useFunctions, true], 
        useTimeZone, 
        [useTimeSuffix, { suffix: ['00:00:00', '23:59:59'] }],
        useCustomFormat,
    ])(props);

    return <BeeRangePicker {...props} placeholder={toArray(props.dateInputPlaceholder)} />;
});

/**
 * 1、时间默认为00:00:00-23:59:59，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const RangePickerTranslate = useLocale(function(props) {
    props = adapterCreator([
        [useFieldId, { range: true }],
        useDefaultProps,  
        [useFunctions, true], 
        useTimeZone, 
        [useTimeSuffix, { suffix: ['00:00:00', '23:59:59'] }],
        useCustomFormat,
    ])(props);
    return <BeeRangePicker {...props} />;
});

/**
 * 1、时间默认为00:00:00-00:00:00，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const TZDatePickerStartBetween = useLocale(function(props) {
    props = adapterCreator([
        [useFieldId, { range: true }],
        useDefaultProps,  
        [useFunctions, true, 'start'], 
        useTimeZone, 
        [useTimeSuffix, { suffix: ['00:00:00', '00:00:00'] }],
        useCustomFormat,
    ])(props);
    return <BeeRangePicker {...props} />;
});

/**
 * 1、时间默认为23:59:59-23:59:59，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const TZDatePickerEndBetween = useLocale(function(props) {
    props = adapterCreator([
        [useFieldId, { range: true }],
        useDefaultProps,  
        [useFunctions, true, 'end'], 
        useTimeZone, 
        [useTimeSuffix, { suffix: ['23:59:59', '23:59:59'] }],
        useCustomFormat,
    ])(props);
    return <BeeRangePicker {...props} />;
});

/**
 * 1、时间可选
 * 2、支持自定义格式
 * 3、转时区
 */
export const DateTimePickerBetween = useLocale(function(props) {
    props = { ...props, picker: "dateTime" };
    props = adapterCreator([
        [useFieldId, { range: true }],
        useDefaultProps,  
        [useFunctions, true],
        useTimeZone, 
        useCustomFormat,
    ])(props);
    return <BeeRangePicker {...props} />;
});

/**
 * 1、时间默认为00:00:00-23:59:59，时间不能选
 * 2、支持自定义格式
 * 3、转时区
 */
export const TZDatePickerRangeDay = useLocale(function(props) {
    props = adapterCreator([
        [useFieldId, { range: true }],
        useDefaultProps,  
        [useFunctions, true], 
        useTimeZone, 
        [useTimeSuffix, { suffix: ['00:00:00', '23:59:59'] }],
        useCustomFormat,
    ])(props);
    let {
        className,
        ...others
    } = props;
    return <BeeRangePicker 
        {...others}
        className={`TZDatePickerRangeDay ${className}`}
    />;
});

/**
 * ================================== 时间适配器 ==================================
 */

/**
 * 1、支持自定义格式
 * 2、不转时区
 */
export const Timepicker = useLocale(function(props) {
    props = { ...props, picker: 'time' };
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        useCustomFormat,
    ])(props);
    return <BeeTimePicker {...props} />;
});

/**
 * 1、支持自定义格式
 * 2、不转时区
 */
export const TimepickerClient = useLocale(function(props) {
    props = { ...props, picker: 'time' };
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        useCustomFormat,
    ])(props);
    return <BeeTimePicker {...props} />;
});

/**
 * ================================== 年适配器 ==================================
 */
export const YearPicker = useLocale(function(props) {
    props = { ...props, picker: 'year' };
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        // useCustomFormat,
    ])(props);
    return <BeeYearPicker {...props} />;
});

/**
 * ================================== 月适配器 ==================================
 */
export const MonthPicker = useLocale(function(props) {
    props = { ...props, picker: 'month' };
    props = adapterCreator([
        useFieldId,
        useDefaultProps,  
        // useCustomFormat,
    ])(props);
    return <BeeMonthPicker {...props} />;
});
